import { useState } from "react";
import { ArrowLeft, Eye, Layers, RefreshCw, RotateCcw } from "lucide-react";
import { Alert, Badge, Button, ConfirmModal, Select } from "@/components/ui";
import { useContextApi, type Version } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { DiffView, shortId } from "./DiffView";
import { formatDateTime, useErrorHandler } from "./shared";

/** Compact version picker for the page toolbar. */
export function VersionPicker({ versions, selected, onSelect }: { versions: Version[]; selected?: string; onSelect: (v: string) => void }) {
  const current = versions.find((v) => v.version === selected);
  return (
    <label className="flex min-w-0 items-center gap-2">
      <Layers className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" aria-hidden />
      <span className="sr-only">Versie om te bekijken</span>
      <Select
        value={selected ?? ""}
        onChange={(e) => onSelect(e.target.value)}
        className={cn("h-9 w-full min-w-0 text-sm sm:w-[22rem]", current && !current.live && "border-sl-yellow-dark")}
        disabled={!versions.length}
        title={current ? `${current.version}${current.note ? ` · ${current.note}` : ""}` : undefined}
      >
        {versions.length === 0 && <option value="">Geen versies</option>}
        {versions.map((v) => (
          <option key={v.version} value={v.version}>
            {v.live ? "● Live · " : ""}
            {formatDateTime(v.updated_at)} · {shortId(v.version)} · {v.note ?? ""}
          </option>
        ))}
      </Select>
    </label>
  );
}

/** Version history: list on the left, readable diff against the previous version on the right. */
export function VersionsView({
  versions,
  viewing,
  onView,
  onRefresh,
  refreshing,
  onOpenTable,
}: {
  versions: Version[];
  viewing?: string;
  onView: (v: string) => void;
  onRefresh: () => Promise<void> | void;
  refreshing?: boolean;
  onOpenTable: (fqn: string) => void;
}) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const live = versions.find((v) => v.live);
  const [picked, setPicked] = useState<string>(viewing ?? live?.version ?? versions[0]?.version ?? "");
  const older = (v: string) => {
    const i = versions.findIndex((x) => x.version === v);
    const prev = (versions[i] as Version & { previous_version?: string })?.previous_version;
    return prev && versions.some((x) => x.version === prev) ? prev : (versions[i + 1]?.version ?? "");
  };
  const isNewer = (v: string) => {
    const x = versions.find((y) => y.version === v);
    return !!x && !!live && !x.live && new Date(x.updated_at) > new Date(live.updated_at);
  };
  // Unpublished newer versions compare against live by default (what would change if made live).
  const defaultBase = (v: string) => (isNewer(v) && live ? live.version : older(v));
  const [pair, setPair] = useState<[string, string]>(() => [defaultBase(picked), picked]);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const pick = (v: string) => {
    setPicked(v);
    setPair([defaultBase(v), v]);
    setMobileDetail(true);
  };
  const cur = versions.find((v) => v.version === picked);
  const rel = (v: Version) => (v.live || !live ? null : new Date(v.updated_at) > new Date(live.updated_at) ? "newer" : "older");

  const doRollback = async () => {
    if (!cur) return;
    setRollingBack(true);
    setNotice(null);
    try {
      await api.rollback(cur.version);
      setConfirmOpen(false);
      setNotice({ tone: "success", text: `Versie ${shortId(cur.version)} (${formatDateTime(cur.updated_at)}) is nu live voor alle agents.` });
      await onRefresh();
    } catch (e) {
      setConfirmOpen(false);
      const msg = handleError(e);
      if (msg) setNotice({ tone: "danger", text: `Terugdraaien mislukt: ${msg}` });
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <aside className={cn("flex min-h-0 flex-1 flex-col bg-card lg:w-[320px] lg:flex-none lg:border-r lg:border-border xl:w-[340px]", mobileDetail && "hidden lg:flex")}>
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-sm font-semibold">Versies ({versions.length})</span>
          <Button variant="ghost" size="icon" onClick={() => onRefresh()} aria-label="Versies vernieuwen" title="Versies vernieuwen" disabled={refreshing}>
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </Button>
        </div>
        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto" aria-label="Versies">
          {versions.map((v) => (
            <li key={v.version}>
              <button
                type="button"
                onClick={() => pick(v.version)}
                aria-current={picked === v.version || undefined}
                className={cn(
                  "block w-full px-4 py-2.5 text-left transition-colors hover:bg-sl-grey-50",
                  picked === v.version && "bg-accent shadow-[inset_3px_0_0_var(--color-sl-yellow)] hover:bg-accent",
                )}
              >
                <span className="flex items-baseline gap-2 whitespace-nowrap">
                  <span className="text-sm font-medium tabular-nums">{formatDateTime(v.updated_at)}</span>
                  <span className="font-mono text-xs text-muted-foreground">{shortId(v.version)}</span>
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                  {v.live && <Badge tone="success">Live</Badge>}
                  {rel(v) === "newer" && <Badge tone="info">Nieuwer dan live</Badge>}
                  {viewing === v.version && !v.live && <Badge tone="warning">Wordt bekeken</Badge>}
                  <span className="min-w-0 truncate text-[13px] text-muted-foreground">{v.note ?? "–"}</span>
                </span>
                <span className="mt-0.5 block truncate font-mono text-[11px] text-muted-foreground">
                  {v.n_tables} tabellen · {v.n_concepts} begrippen
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div className={cn("min-h-0 min-w-0 flex-1 flex-col bg-card", mobileDetail ? "flex" : "hidden lg:flex")}>
        {cur && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 lg:px-6">
            <div className="min-w-0">
              <button type="button" onClick={() => setMobileDetail(false)} className="mb-1 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground lg:hidden">
                <ArrowLeft className="h-4 w-4" />
                Alle versies
              </button>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{formatDateTime(cur.updated_at)}</h2>
                <span className="font-mono text-sm text-muted-foreground">{shortId(cur.version)}</span>
                {cur.live ? <Badge tone="success">Live</Badge> : rel(cur) === "newer" ? <Badge tone="info">Nieuwer dan live, niet gepubliceerd</Badge> : <Badge tone="neutral">Ouder dan live</Badge>}
              </div>
              <p className="truncate text-sm text-muted-foreground">
                {cur.note ?? "Geen notitie"} · <span className="font-mono text-xs">{cur.version}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!cur.live && live && (
                <div className="inline-flex rounded-md border border-border p-0.5 text-[13px]" role="group" aria-label="Vergelijk met">
                  {(
                    [
                      [live.version, "t.o.v. live"],
                      [older(cur.version), "t.o.v. vorige"],
                    ] as [string, string][]
                  )
                    .filter(([v], i, arr) => v && (i === 0 || v !== arr[0][0]))
                    .map(([v, l]) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setPair([v, cur.version])}
                        aria-pressed={pair[0] === v && pair[1] === cur.version}
                        className={cn("h-7 rounded px-2.5 font-medium", pair[0] === v && pair[1] === cur.version ? "bg-sl-charcoal text-white" : "text-muted-foreground hover:text-foreground")}
                      >
                        {l}
                      </button>
                    ))}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => onView(cur.version)} disabled={viewing === cur.version}>
                <Eye className="h-4 w-4" />
                {viewing === cur.version ? "Wordt bekeken" : "Bekijk in catalogus"}
              </Button>
              {!cur.live && (
                <Button variant="danger" size="sm" onClick={() => setConfirmOpen(true)}>
                  <RotateCcw className="h-4 w-4" />
                  Maak deze versie live
                </Button>
              )}
            </div>
          </div>
        )}
        {notice && (
          <div className="px-5 pt-3 lg:px-6">
            <Alert tone={notice.tone} onClose={() => setNotice(null)}>
              {notice.text}
            </Alert>
          </div>
        )}
        <DiffView versions={versions} from={pair[0]} to={pair[1]} onChange={(f, t) => setPair([f, t])} onOpenTable={onOpenTable} />
      </div>

      <ConfirmModal
        open={confirmOpen}
        title="Deze versie live maken?"
        message={
          cur ? (
            <>
              <span className="block">
                Nu live: <span className="font-medium text-foreground">{live ? `${formatDateTime(live.updated_at)} · ${shortId(live.version)}` : "–"}</span>
              </span>
              <span className="block">
                Wordt live: <span className="font-medium text-foreground">{`${formatDateTime(cur.updated_at)} · ${shortId(cur.version)}`}</span> ({cur.note ?? "geen notitie"})
              </span>
              <span className="mt-2 block">Alle agents gebruiken direct deze versie. Je kunt dit later weer omzetten.</span>
            </>
          ) : null
        }
        confirmLabel="Maak live"
        tone="danger"
        loading={rollingBack}
        onConfirm={doRollback}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
